import type { ReactNode } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card'
import { Badge } from '@/components/ui/shadcn/badge'
import { cn } from '@/lib/utils'
import DataPointInfoButton from './DataPointInfoButton'

interface SummaryMetricCardProps {
  title: string
  value: string | number
  subtitle?: string
  delta?: number
  deltaLabel?: string
  icon?: ReactNode
  chart?: ReactNode
  className?: string
  intent?: 'neutral' | 'success' | 'warning' | 'danger'
  /** Key into AnalyticsDocsContext; renders the (i) button when a doc exists. */
  docKey?: string
}

export function SummaryMetricCard({
  title,
  value,
  subtitle,
  delta,
  deltaLabel,
  icon,
  chart,
  className,
  intent = 'neutral',
  docKey
}: SummaryMetricCardProps) {
  const deltaColor =
    intent === 'success' || (delta !== undefined && delta > 0) ? 'text-success-subtle-foreground' :
    intent === 'danger' || (delta !== undefined && delta < 0) ? 'text-destructive' :
    'text-muted-foreground'

  const deltaPrefix = delta !== undefined ? (delta > 0 ? '+' : '') : ''

  return (
    <Card className={cn('relative overflow-hidden', className)}>
      <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
        <CardTitle className='text-sm font-medium'>{title}</CardTitle>
        <div className='flex shrink-0 items-center gap-1'>
          <DataPointInfoButton docKey={docKey} />
          {icon && (
            <div className='text-muted-foreground'>
              {icon}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className='text-2xl font-bold'>{value}</div>
        {subtitle && (
          <p className='text-muted-foreground text-xs mt-1'>{subtitle}</p>
        )}
        {delta !== undefined && (
          <div className='flex items-center gap-2 mt-2'>
            <Badge 
              variant={intent === 'success' || delta > 0 ? 'default' : intent === 'danger' || delta < 0 ? 'destructive' : 'secondary'}
              className='text-xs'
            >
              {deltaPrefix}{delta.toFixed(1)}%
            </Badge>
            {deltaLabel && (
              <span className={cn('text-xs', deltaColor)}>{deltaLabel}</span>
            )}
          </div>
        )}
        {chart && (
          <div className='mt-4'>
            {chart}
          </div>
        )}
      </CardContent>
    </Card>
  )
}




